import { LEAVES_BG_XS, LEAVES_BG_MD } from '../../config/backgrounds';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Button,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalanceWallet as MoneyIcon,
  Business as BusinessIcon,
  CheckCircle as TaskIcon,
  Schedule as TimelineIcon,
  TableRestaurant as SeatingIcon,
  NavigateNext as NavigateNextIcon,
  Event as EventIcon,
  MailOutline as MailIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  Place as PlaceIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import weddingEventService from '../../services/weddingEventService';

import EditWeddingEventDialog from '../../components/WeddingPlanner/EditWeddingEventDialog';
import EventDashboardTab from '../../components/WeddingPlanner/EventDashboardTab';
import GuestsTab from '../../components/WeddingPlanner/GuestsTab';
import BudgetTab from '../../components/WeddingPlanner/BudgetTab';
import VendorsTab from '../../components/WeddingPlanner/VendorsTab';
import TasksTab from '../../components/WeddingPlanner/TasksTab';
import TimelineTab from '../../components/WeddingPlanner/TimelineTab';
import SeatingTab from '../../components/WeddingPlanner/SeatingTab';
import InvitationsTab from '../../components/WeddingPlanner/InvitationsTab';

// A fülek kulccsal azonosítottak (nem indexszel), így a sorrend szabadon változtatható.
// Az Időbeosztás még csak helyőrző, ezért a sor végére került „Hamarosan" jelöléssel.
const TABS = [
  { key: 'overview', icon: <DashboardIcon />, label: 'Áttekintés', shortLabel: 'Infó' },
  { key: 'vendors', icon: <BusinessIcon />, label: 'Szolgáltatók', shortLabel: 'Szolgált.' },
  { key: 'budget', icon: <MoneyIcon />, label: 'Költségvetés', shortLabel: 'Büdzsé' },
  { key: 'guests', icon: <PeopleIcon />, label: 'Vendégek', shortLabel: 'Vendégek' },
  { key: 'tasks', icon: <TaskIcon />, label: 'Feladatok', shortLabel: 'Teendők' },
  { key: 'seating', icon: <SeatingIcon />, label: 'Ültetési rend', shortLabel: 'Ültetés' },
  { key: 'invitations', icon: <MailIcon />, label: 'Meghívók', shortLabel: 'Meghívó' },
  { key: 'timeline', icon: <TimelineIcon />, label: 'Időbeosztás', shortLabel: 'Időrend', comingSoon: true },
];

// Az arany (secondary.main) a krém háttéren túl halvány szöveghez — sötétebb árnyalat
const DAYS_LEFT_COLOR = '#8a6212';

const STATUS_COLOR = { PLANNING: 'warning', CONFIRMED: 'success', COMPLETED: 'default', CANCELLED: 'error' };
const STATUS_LABEL = { PLANNING: 'Tervezés alatt', CONFIRMED: 'Megerősítve', COMPLETED: 'Lezajlott', CANCELLED: 'Lemondva' };

function WeddingEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Az aktív fül az URL-ben (?tab=budget): újratöltés, vissza-gomb és megosztott link után is ott marad
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const currentTab = TABS.some((t) => t.key === tabParam) ? tabParam : 'overview';
  const setCurrentTab = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'overview') next.delete('tab'); else next.set('tab', key);
      return next;
    }, { replace: true });
  };
  const [highlightedGuestId, setHighlightedGuestId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleJumpToSeating = (guestId) => {
    setHighlightedGuestId(guestId);
    setCurrentTab('seating');
  };

  const [resettingDemo, setResettingDemo] = useState(false);
  const handleResetDemo = async () => {
    if (!window.confirm('Visszaállítod a bemutató esküvőt az eredeti állapotára? Az itt végzett módosítások elvesznek.')) return;
    setResettingDemo(true);
    try {
      const res = await weddingEventService.resetDemo();
      if (res?.data?.id) navigate(`/wedding-planner/${res.data.id}${tabParam ? `?tab=${tabParam}` : ''}`, { replace: true });
    } catch (err) {
      window.alert('Nem sikerült visszaállítani a bemutatót.');
    } finally {
      setResettingDemo(false);
    }
  };

  const handleUpdateEvent = async (updateData) => {
    await weddingEventService.updateEvent(parseInt(eventId), updateData);
    setEditDialogOpen(false);
    fetchEvent();
  };

  useEffect(() => {
    fetchEvent({ initial: true });
  }, [eventId]);

  // Teljes oldalas töltőképernyő csak az első betöltéskor: a fülek onRefresh hívásai
  // különben az egész oldalt (fülsort, nyitott fület) lecserélnék egy spinnerre.
  const fetchEvent = async ({ initial = false } = {}) => {
    try {
      if (initial) setLoading(true);
      const response = await weddingEventService.getEvent(parseInt(eventId));
      if (response.success) {
        setEvent(response.data);
      } else {
        setError('Nem sikerült betölteni az eseményt.');
      }
    } catch (err) {
      console.error('Fetch event error:', err);
      setError('Hiba történt az esemény betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const pageBackground = {
    minHeight: '100vh',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundImage: {
        xs: LEAVES_BG_XS,
        md: LEAVES_BG_MD,
      },
      backgroundSize: { xs: '100% auto', md: 'cover' },
      backgroundPosition: { xs: 'center top', md: 'center center' },
      backgroundRepeat: 'no-repeat',
      zIndex: -1,
      willChange: 'transform',
      transform: 'translateZ(0)',
    },
  };

  if (loading) {
    return (
      <Box sx={pageBackground}>
        <Container maxWidth="xl" sx={{ pt: 8, textAlign: 'center' }}>
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2, color: 'primary.dark' }}>Betöltés...</Typography>
        </Container>
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box sx={pageBackground}>
        <Container maxWidth="xl" sx={{ pt: 4 }}>
          <Alert severity="error" sx={{ borderRadius: '12px' }}>
            {error || 'Az esemény nem található.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  const metaItemSx = { display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', typography: 'body2' };
  const statusColor = STATUS_COLOR[event.status] || 'default';
  const statusLabel = STATUS_LABEL[event.status] || event.status;

  return (
    <Box sx={pageBackground}>
      {/* Az ültetési vászonnak több hely jár: ott szélesebb a konténer */}
      <Container maxWidth={currentTab === 'seating' ? 'xl' : 'lg'} sx={{ pt: 3, pb: { xs: 10, md: 6 } }}>

        {/* Breadcrumbs */}
        {/* Breadcrumbs — mobilon csak visszalink, mert a név úgyis ott van a fejlécben */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
          sx={{ mb: { xs: 1.5, md: 2 }, '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' }, '& .MuiBreadcrumbs-li:last-of-type': { minWidth: 0 } }}
        >
          <Link
            component={RouterLink}
            to="/wedding-planner"
            underline="hover"
            sx={{ color: 'primary.main', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
          >
            {isMobile && <ArrowBackIcon sx={{ fontSize: 18 }} />}
            Esküvő Tervező
          </Link>
          {!isMobile && (
            <Typography color="text.primary" fontWeight={600} noWrap title={event.name}>{event.name}</Typography>
          )}
        </Breadcrumbs>

        {/* Hero fejléc */}
        <Paper elevation={0} sx={{
          mb: { xs: 2, md: 3 },
          p: { xs: 2, md: 3 },
          borderRadius: '24px',
          position: 'relative',
          overflow: 'hidden',
          background: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: 'blur(12px)',
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.30)}`,
          boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.10)}`,
        }}>
          {/* Arany bal oldali dekorációs csík */}
          <Box sx={{
            position: 'absolute',
            left: 0, top: '10%', bottom: '10%',
            width: 4,
            background: `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
            borderRadius: '0 4px 4px 0',
          }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
              <Typography
                variant="h4"
                component="h1"
                title={event.name}
                sx={{
                  color: 'primary.dark', mb: 0.25, fontStyle: 'italic',
                  fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2rem' },
                  overflowWrap: 'anywhere',
                  // Nagyon hosszú név: legfeljebb 2 sor, hogy a fejléc ne egye meg a képernyőt
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}
              >
                {event.name}
              </Typography>
              {(() => {
                const daysUntil = Math.ceil((new Date(event.eventDate) - new Date()) / 86400000);
                return daysUntil > 0 ? (
                  <Typography variant="caption" sx={{ color: DAYS_LEFT_COLOR, fontWeight: 700, letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
                    {daysUntil} nap van hátra
                  </Typography>
                ) : null;
              })()}
              {/* Minden adat külön, nem törő egység — így az ikon nem szakad el a szövegétől */}
              <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 0.25, flexWrap: 'wrap', color: 'text.secondary' }}>
                <Box component="span" sx={metaItemSx}>
                  <EventIcon sx={{ fontSize: 16 }} />
                  {format(new Date(event.eventDate), 'yyyy. MMMM dd. (EEEE)', { locale: hu })}
                </Box>
                {event.eventTime && (
                  <Box component="span" sx={metaItemSx}>
                    <TimeIcon sx={{ fontSize: 15 }} />
                    {event.eventTime}
                  </Box>
                )}
                {event.venueName && (
                  <Box component="span" sx={{ ...metaItemSx, display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'normal', minWidth: 0 }}>
                    <PlaceIcon sx={{ fontSize: 16 }} />
                    {event.venueName}
                  </Box>
                )}
                {event.estimatedGuests && (
                  <Box component="span" sx={{ ...metaItemSx, display: { xs: 'none', sm: 'inline-flex' } }}>
                    <PeopleIcon sx={{ fontSize: 16 }} />
                    {event.estimatedGuests} fő tervezett
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Esemény szerkesztése">
                <IconButton
                  onClick={() => setEditDialogOpen(true)}
                  size="small"
                  sx={{ color: 'primary.main', border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Státusz-címke, nem gomb: halvány kitöltés, hogy ne tűnjön kattinthatónak */}
              <Chip
                label={statusLabel}
                color={statusColor}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600, borderRadius: '8px', bgcolor: statusColor === 'default' ? undefined : alpha(theme.palette[statusColor].main, 0.08) }}
              />
            </Box>
          </Box>
        </Paper>

        {event.isDemo && (
          <Alert
            severity="info"
            icon={false}
            sx={{
              mb: { xs: 2, md: 3 }, borderRadius: '14px', alignItems: 'center',
              bgcolor: alpha(theme.palette.secondary.main, 0.14), color: 'primary.dark',
              border: `1px dashed ${theme.palette.secondary.main}`,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>Ez egy bemutató esküvő</Typography>
                <Typography variant="body2">
                  Bátran próbálj ki bármit — szerkessz, ültess, törölj. Meghívó innen nem megy ki, és a foglalásaid sem kerülnek ide.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={handleResetDemo} disabled={resettingDemo}>
                  {resettingDemo ? 'Visszaállítás…' : 'Visszaállítás'}
                </Button>
                <Button size="small" variant="contained" onClick={() => navigate('/wedding-planner', { state: { openCreate: true } })}>
                  Saját esküvő létrehozása
                </Button>
              </Box>
            </Box>
          </Alert>
        )}

        {/* Tab panel */}
        <Paper elevation={0} sx={{
          borderRadius: '20px',
          background: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(12px)',
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.20)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.dark, 0.08)}`,
        }}>
          {/* Tabs sor */}
          <Box sx={{
            position: 'sticky',
            top: 64,
            zIndex: 100,
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.10)} 100%)`,
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.96),
            borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.20)}`,
            borderRadius: '20px 20px 0 0',
          }}>
            <Tabs
              value={currentTab}
              onChange={(_, v) => setCurrentTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              // Mobilon is látszódjon a nyíl, hogy egyértelmű: oldalra görgethető a fülsor
              allowScrollButtonsMobile
              sx={{
                px: { xs: 0, sm: 1 },
                '& .MuiTabs-scrollButtons': { width: { xs: 28, sm: 40 } },
                '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.25 },
                '& .MuiTab-root': {
                  minHeight: { xs: 60, sm: 64 },
                  minWidth: { xs: 64, sm: 80 },
                  fontWeight: 600,
                  fontSize: { xs: '0.6875rem', sm: '0.8125rem' },
                  color: 'text.secondary',
                  borderRadius: '8px',
                  mx: { xs: 0.25, md: 0 },
                  px: { xs: 0.5, sm: 1 },
                  transition: 'background-color 0.18s ease, color 0.18s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.light, 0.22),
                  },
                  '&.Mui-selected': {
                    color: 'primary.dark',
                    backgroundColor: alpha(theme.palette.secondary.light, 0.35),
                  },
                },
                '& .MuiTabs-indicator': {
                  top: 0,
                  bottom: 'auto',
                  height: 3,
                  borderRadius: '0 0 4px 4px',
                  backgroundColor: 'secondary.main',
                },
              }}
            >
              {TABS.map((tab) => {
                const label = tab.comingSoon && !isMobile ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    {tab.label}
                    <Chip label="Hamarosan" size="small" sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }} />
                  </Box>
                ) : (isMobile ? tab.shortLabel : tab.label);
                return (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    icon={tab.icon}
                    label={label}
                    aria-label={tab.label}
                    iconPosition={isMobile ? 'top' : 'start'}
                    sx={tab.comingSoon ? { opacity: 0.7 } : undefined}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Tab tartalom – ültetési rendnél xs-en nincs padding, hogy a vászon nagyobb legyen */}
          <Box sx={{
            p: { xs: currentTab === 'seating' ? 0 : 2, sm: 3, md: 4 },
            // Fülváltáskor, amíg az új fül tölt, ne essen össze a doboz (a lábléc ne ugráljon)
            minHeight: { xs: '60vh', md: 520 },
            bgcolor: theme.palette.background.default,
            borderRadius: '0 0 20px 20px',
            animation: 'fadeIn 0.2s ease',
            // Csak opacity animál — transform itt új "containing block"-ot hozna létre ennek a
            // Box-nak, ami eltéríti az Ültetésrendező position:fixed alapú mobil fullscreen
            // fallback-jét (a "fixed" elem ekkor ehhez az őshöz, nem a viewporthoz igazodna).
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}>
            {currentTab === 'overview' && <EventDashboardTab event={event} onRefresh={fetchEvent} onTabChange={setCurrentTab} />}
            {currentTab === 'vendors' && <VendorsTab event={event} onRefresh={fetchEvent} />}
            {currentTab === 'budget' && <BudgetTab event={event} onRefresh={fetchEvent} />}
            {currentTab === 'guests' && <GuestsTab event={event} onRefresh={fetchEvent} onJumpToSeating={handleJumpToSeating} />}
            {currentTab === 'tasks' && <TasksTab event={event} onRefresh={fetchEvent} />}
            {currentTab === 'seating' && <SeatingTab event={event} onRefresh={fetchEvent} highlightedGuestId={highlightedGuestId} />}
            {currentTab === 'invitations' && <InvitationsTab event={event} onRefresh={fetchEvent} />}
            {currentTab === 'timeline' && <TimelineTab event={event} onRefresh={fetchEvent} />}
          </Box>
        </Paper>

      </Container>

      <EditWeddingEventDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdateEvent}
        event={event}
      />
    </Box>
  );
}

export default WeddingEventDetail;
